import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

export interface Product {
  // Nom du produit
  title: string;
  // Explication de l'acronyme s'il existe
  subtitle?: string;
  // Question qu'est ce que + produit ?
  bodyTitle: string;
  // Réponse à la question, explication du produit
  body: string;
  // Secteur immo ou financier, pour le filtre
  sector: string[];
  // Objectifs du produit ex patromoine, diversification, etc. Filtre + affichage en badge
  objectives: string[];
  // Niveau de risque moyen
  riskRange: any[];
  // Lien vers la simu si elle existe
  routerLink?: string;
  // Faux quand le produit n'a pas de fiche pédagogique
  hasDocs?: boolean;
  queryParams?: any;
  // Couleur d'action pour la page infos
  color?: string;
  // Nom avec pronom du produit
  titleLink?: string;
  // Image du produit
  srcImg?: string;
  // Image du produit resp
  srcImgResp?: string;
}

export enum ProductsType {
  SCPI = 'SCPI',
  PME = 'PME',
  PER = 'PER',
  PERP = 'PERP',
  PEA = 'PEA',
  PEA_PME = 'PEA / PME',
  PEE = 'PEE',
  PEI = 'PEI',
  PERCO = 'PERCO',
  PINEL = 'PINEL',
  ASSURANCE_VIE = 'ASSURANCE VIE',
  FCPI = 'FCPI',
  FIP = 'FIP',
  GIRARDIN = 'GIRARDIN',
  INVESTISSEMENT_PME = 'INVESTISSEMENT PME',
  SOFICA = 'SOFICA',
  LMNP = 'LMNP',
  BIC = 'BIC',
  PREFON = 'PREFON',
  COMPTES_TITRES = 'COMPTE-TITRES',
  TANGIBLES = 'TANGIBLES',
  CROWDFUNDING = 'CROWDFUNDING',
  DEFISCALISATION = 'DEFISCALISATION',
  FISCALITY_RENT = 'FISCALITE IMMOBILIERE',
  CONTRAT_CAPI = 'CONTRAT DE CAPITALISATION',
  UNFURNISHED = 'UNFURNISHED',
}

export enum ProductsObjective {
  WEALTH_OBJECTIVE = 'PATRIMOINE',
  DEFISC_OBJECTIVE = 'DEFISC',
  DIVERSIFICATION_OBJECTIVE = 'DIVERS',
  INCOME_OBJECTIVE = 'REVENUS',
  OWNER_OBJECTIVE = 'OWNER',
  RENT_OBJECTIVE = 'RENT',
  RETIREMENT_OBJECTIVE = 'RETIREMENT',
  TRANSMISSION_OBJECTIVE = 'TRANSMISSION',
}

export enum ProductSector {
  ASSET_SECTOR = 'asset',
  ESTATE_SECTOR = 'estate',
}

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  sectors: any[] = [
    { id: ProductSector.ESTATE_SECTOR, label: 'Immobilier' },
    { id: ProductSector.ASSET_SECTOR, label: 'Financier' },
  ];

  fiscalityObject = {
    title: ProductsType.FISCALITY_RENT,
    bodyTitle: "Qu’est ce qu'une optimisation fiscale ?",
    body: 'Le simulateur des régimes fiscaux de location meublée est un outil qui permet de comparer les différents régimes en France. En entrant les informations relatives à vos différentes locations meublée, le simulateur va calculer le montant des impôts à payer pour chaque régime fiscal disponible et vous recommander le régime fiscal le plus adapté à votre situation.',
    objectives: [ProductsObjective.DEFISC_OBJECTIVE],
    riskRange: [4, 4],
    sector: [ProductSector.ESTATE_SECTOR],
    routerLink: '/dashboard/sheets/$route/fiscality-rent',
    color: '#73A4F6',
    hasDocs: false,
  };

  products: Product[] = [
    {
      title: ProductsType.SCPI,
      subtitle: '(société civile de placement immobilier)',
      bodyTitle: 'Qu’est ce que la SCPI ?',
      body: 'La SCPI (Société Civile de placement immobilier) permet à tous les investisseurs d’accéder au secteur de l’immobilier. En achetant les parts d’une SCPI, vous devenez associé et recevez des dividendes au prorata de votre investissement. De par la diversité de son parc immobilier, la SCPI permet de réduire les risques liés à un investissement locatif. ',
      objectives: [
        ProductsObjective.WEALTH_OBJECTIVE,
        ProductsObjective.DEFISC_OBJECTIVE,
        ProductsObjective.INCOME_OBJECTIVE,
      ],
      riskRange: [2, 5],
      sector: [ProductSector.ESTATE_SECTOR],
      routerLink: '/dashboard/sheets/$route/scpi',
      color: '#FF6A4C',
      titleLink: 'la SCPI',
      srcImg: 'assets/img/obj-scpi.png',
      srcImgResp: 'assets/img/obj-scpi-mob.png',
    },
    {
      title: ProductsType.ASSURANCE_VIE,
      bodyTitle: 'Qu’est ce qu’une assurance-vie ?',
      body: 'Une assurance vie est un contrat dédié aux investisseurs souhaitant épargner pour faire fructifier son épargne, bénéficier d’un complément de retraite ou transmettre un héritage. Il s’agit d’une enveloppe fiscale permettant de loger des placements financiers plus ou moins risqués. Les versements peuvent s’effectuer de manière unitaire ou périodique. ',
      objectives: [
        ProductsObjective.RENT_OBJECTIVE,
        ProductsObjective.RETIREMENT_OBJECTIVE,
        ProductsObjective.TRANSMISSION_OBJECTIVE,
      ],
      riskRange: [1, 7],
      sector: [ProductSector.ASSET_SECTOR],
      routerLink: '/dashboard/sheets/$route/av',
      color: '#C24694',
      titleLink: "l'assurance-vie",
      srcImg: 'assets/img/obj-av.png',
      srcImgResp: 'assets/img/obj-av-mob.png',
    },
    {
      title: ProductsType.PER,
      subtitle: '(Plan d’épargne retraite)',
      bodyTitle: 'Qu’est ce que le PER ?',
      body: 'Le plan d’épargne retraite (Ancien PERP) est un placement à long terme permettant de bénéficier d’un avantage fiscal au moment d’un versement puis d’une rente viagère à partir du départ à la retraite. Comme pour une assurance-vie, l’épargnant a le choix entre de nombreux supports, lui permettant de gérer le niveau de risque de son PER.',
      objectives: [
        ProductsObjective.RETIREMENT_OBJECTIVE,
        ProductsObjective.TRANSMISSION_OBJECTIVE,
        ProductsObjective.RENT_OBJECTIVE,
      ],
      riskRange: [1, 7],
      sector: [ProductSector.ASSET_SECTOR],
      routerLink: '/dashboard/sheets/$route/per',
      titleLink: 'le PER',
      srcImg: 'assets/img/obj-per.png',
      srcImgResp: 'assets/img/obj-per-mob.png',
    },
    {
      title: ProductsType.PEA,
      subtitle: "(Plan d'épargne en actions)",
      bodyTitle: 'Qu’est ce que le PEA ?',
      body: "Le Plan d'Epargne en Actions est un produit d'investissement destiné aux particuliers qui souhaitent investir en bourse sur le long terme. Il permet d'investir dans des actions françaises ou européennes tout en bénéficiant d'un avantage fiscal. Les plus-values réalisées à l'issue d'un délai de 5 ans sont exonérées d'impôt sur le revenu, sous réserve de respecter certaines conditions. Les sommes investies dans un PEA sont bloquées pendant une durée minimale de 5 ans.",
      objectives: [
        ProductsObjective.DIVERSIFICATION_OBJECTIVE,
        ProductsObjective.INCOME_OBJECTIVE,
        ProductsObjective.OWNER_OBJECTIVE,
      ],
      riskRange: [3, 7],
      sector: [ProductSector.ASSET_SECTOR],
      // routerLink: "/dashboard/sheets/$route/pea",
      titleLink: 'le PEA',
      srcImg: 'assets/img/obj-pea.png',
      srcImgResp: 'assets/img/obj-pea-mob.png',
    },
    {
      title: ProductsType.PEA_PME,
      subtitle: "(Plan d'épargne en actions destiné au financement des PME)",
      bodyTitle: "Qu'est-ce que le PEA-PME ?",
      body: "Le Plan d'Epargne en Actions destiné au financement des PME (PEA-PME) est un produit d'investissement qui permet d'investir en bourse sur le long terme dans des PME et ETI (Entreprises de Taille Intermédiaire) françaises et européennes. Les sommes investies dans un PEA-PME bénéficient d'un avantage fiscal et les plus-values réalisées à l'issue d'un délai de 5 ans sont exonérées d'impôt sur le revenu, sous certaines conditions. Le PEA-PME est soumis aux mêmes règles que le PEA, notamment en termes de plafond de versement et de durée minimale de blocage des sommes investies.",
      objectives: [
        ProductsObjective.DIVERSIFICATION_OBJECTIVE,
        ProductsObjective.INCOME_OBJECTIVE,
        ProductsObjective.OWNER_OBJECTIVE,
      ],
      riskRange: [4, 7],
      sector: [ProductSector.ASSET_SECTOR],
      //routerLink: "/dashboard/sheets/$route/pea-pme",
      titleLink: 'le PEA-PME',
      srcImg: 'assets/img/obj-pea-pme.png',
      srcImgResp: 'assets/img/obj-pea-pme-mob.png',
    },
    {
      title: ProductsType.PEE,
      subtitle: "(Plan d'épargne entreprise)",
      bodyTitle: "Qu'est-ce que le PEE ?",
      body: "Le Plan d'Epargne Entreprise est un dispositif d'épargne salariale proposé par les entreprises à leurs salariés. Il permet aux salariés de se constituer une épargne à moyen et long terme en bénéficiant de l'abondement de l'entreprise et d'avantages fiscaux. Les sommes investies dans un PEE sont bloquées pendant une durée minimale de 5 ans et peuvent être récupérées sous forme de capital ou de rente viagère au moment de la retraite.",
      objectives: [
        ProductsObjective.DIVERSIFICATION_OBJECTIVE,
        ProductsObjective.INCOME_OBJECTIVE,
        ProductsObjective.RETIREMENT_OBJECTIVE,
        ProductsObjective.TRANSMISSION_OBJECTIVE,
      ],
      riskRange: [2, 5],
      sector: [ProductSector.ASSET_SECTOR],
      //routerLink: "/dashboard/sheets/$route/per",
      titleLink: 'le PEE',
      srcImg: 'assets/img/obj-pee.png',
      srcImgResp: 'assets/img/obj-pee-mob.png',
    },
    {
      title: ProductsType.PERCO,
      subtitle: "(Plan d'épargne retraite collectif)",
      bodyTitle: "Qu'est-ce que le PERCO ?",
      body: "Le Plan d'Epargne Retraite Collectif est un dispositif d'épargne salariale proposé par les entreprises à leurs salariés. Il permet de se constituer une épargne à long terme pour la retraite, en bénéficiant de l'abondement de l'entreprise et d'avantages fiscaux. Les sommes investies dans un PERCO sont bloquées jusqu'au départ à la retraite et peuvent être récupérées sous forme de capital ou de rente viagère.",
      objectives: [
        ProductsObjective.RETIREMENT_OBJECTIVE,
        ProductsObjective.TRANSMISSION_OBJECTIVE,
      ],
      riskRange: [2, 5],
      sector: [ProductSector.ASSET_SECTOR],
      //routerLink: "/dashboard/sheets/$route/perco",
      titleLink: 'le PERCO',
    },
    // {
    //   title: ProductsType.PINEL,
    //   bodyTitle: 'Qu’est ce que la loi Pinel ?',
    //   body: 'La loi Pinel offre une réduction fiscale pour tout investissement locatif dans un bien immobilier neuf respectant des plafonds de loyers et de ressources pour le locataire. Les avantages fiscaux liés à la loi Pinel dépendent de la durée de détention et de mise en location du bien.',
    //   objectives: [
    //     ProductsObjective.WEALTH_OBJECTIVE,
    //     ProductsObjective.DEFISC_OBJECTIVE,
    //     ProductsObjective.OWNER_OBJECTIVE,
    //   ],
    //   riskRange: [3, 3],
    //   sector: [ProductSector.ESTATE_SECTOR],
    //   routerLink: '/dashboard/sheets/$route/pinel',
    //   titleLink: 'le PINEL',
    //   srcImg: 'assets/img/obj-pinel.png',
    //   srcImgResp: 'assets/img/obj-pinel-mob.png',
    // },

    {
      title: ProductsType.FCPI,
      bodyTitle: 'Qu’est-ce qu’un FCPI ?',
      body: 'Les FCPI (Fonds Commun de Placement dans l’Innovation) sont des organismes de placement vous permettant d’investir dans des petites et moyennes entreprises innovantes et non cotées en bourse.',
      objectives: [
        ProductsObjective.WEALTH_OBJECTIVE,
        ProductsObjective.DEFISC_OBJECTIVE,
      ],
      riskRange: [6, 6],
      sector: [ProductSector.ASSET_SECTOR],
      routerLink: '/dashboard/sheets/$route/fcpi',
      titleLink: 'la FCPI',
      srcImg: 'assets/img/obj-fcpi-fip.png',
      srcImgResp: 'assets/img/obj-fcpi-fip-mob.png',
    },
    {
      title: ProductsType.FIP,
      bodyTitle: 'Qu’est ce que la FIP ?',
      body: 'Les FIP (Fonds d’Investissement de Proximité) sont des organismes de placement qui permettent d’investir au sein des petites et moyennes entreprises et d’obtenir une réduction d’impôt. Accessible à partir de 1 000€, les FIP permettent d’investir au sein des PME non cotées en bourse.',
      objectives: [
        ProductsObjective.WEALTH_OBJECTIVE,
        ProductsObjective.DEFISC_OBJECTIVE,
      ],
      riskRange: [6, 6],
      sector: [ProductSector.ASSET_SECTOR],
      routerLink: '/dashboard/sheets/$route/fip',
      titleLink: 'la FIP',
      srcImg: 'assets/img/obj-fcpi-fip.png',
      srcImgResp: 'assets/img/obj-fcpi-fip-mob.png',
    },
    {
      title: ProductsType.GIRARDIN,
      bodyTitle: 'Qu’est ce que le Girardin ?',
      body: 'Le Girardin permet d’obtenir une défiscalisation « One Shot » entre 110 et 120% après un investissement à fond perdu. Le principe du Girardin est d’investir dans les départements d’Outre-mer tout en bénéficiant d’une réduction d’impôt avantageuse. Ce système a été mis en place par le gouvernement dans l’optique de soutenir l’activité dans les DOM-TOM.',
      objectives: [ProductsObjective.DEFISC_OBJECTIVE],
      riskRange: [7, 7],
      sector: [ProductSector.ASSET_SECTOR],
      routerLink: '/dashboard/sheets/$route/girardin',
      titleLink: 'le GIRARDIN',
      srcImg: 'assets/img/obj-girardin.png',
      srcImgResp: 'assets/img/obj-girardin-mob.png',
    },
    {
      title: ProductsType.INVESTISSEMENT_PME,
      bodyTitle: 'Qu’est ce qu’un investissement en private equity ? ',
      body: 'Afin d’encourager l’investissement en fonds propres dans le private equity, il est désormais possible pour les personnes physiques d’investir directement et de bénéficier d’une incitation fiscale en matière d’impôt sur le revenu (IR), dès lors qu’ils conservent leurs titres pendant 5 ans.',
      objectives: [
        ProductsObjective.WEALTH_OBJECTIVE,
        ProductsObjective.DEFISC_OBJECTIVE,
      ],
      riskRange: [6, 6],
      sector: [ProductSector.ASSET_SECTOR],
      routerLink: '/dashboard/sheets/$route/pme',
      titleLink: 'Private Equity',
    },
    {
      title: ProductsType.SOFICA,
      subtitle:
        "(Sociétés pour le financement de l'industrie cinématographique et audiovisuelle)",
      bodyTitle: 'Qu’est ce que la SOFICA ?',
      body: 'Les SOFICA, ou sociétés pour le financement de l’industrie cinématographique et audiovisuelle, sont des niches fiscales collectant des fonds privés destinés à financer la production cinématographique et audiovisuelle. Alors si vous souhaitez encourager le 7e Art et participer à son rayonnement, vous êtes au bon endroit !',
      objectives: [
        ProductsObjective.WEALTH_OBJECTIVE,
        ProductsObjective.DIVERSIFICATION_OBJECTIVE,
      ],
      riskRange: [6, 7],
      sector: [ProductSector.ASSET_SECTOR],
      routerLink: '/dashboard/sheets/$route/sofica',
      titleLink: 'la SOFICA',
      srcImg: 'assets/img/obj-sofica.png',
      srcImgResp: 'assets/img/obj-sofica-mob.png',
    },
    {
      title: ProductsType.LMNP,
      subtitle: '(Location meublée non professionnelle)',
      bodyTitle: 'Qu’est-ce que le régime LMNP ?',
      body: 'Le régime LMNP, loueur meublé non professionnel, est un dispositif qui vous permet de défiscaliser vos revenus locatifs. Pour bénéficier de cet avantage il vous suffit de louer votre bien en meublé. Il est accessible à tous et s’adresse aux investisseurs qui souhaitent investir dans un bien immobilier pour se constituer un patrimoine sur le long terme.',
      objectives: [
        ProductsObjective.WEALTH_OBJECTIVE,
        ProductsObjective.OWNER_OBJECTIVE,
        ProductsObjective.RENT_OBJECTIVE,
        ProductsObjective.INCOME_OBJECTIVE,
      ],
      riskRange: [2, 3],
      sector: [ProductSector.ESTATE_SECTOR],
      routerLink: '/dashboard/sheets/$route/lmnp',
      color: '#73A4F6',
      titleLink: 'la LMNP',
      srcImg: 'assets/img/obj-lmnp.png',
      srcImgResp: 'assets/img/obj-lmnp-mob.png',
    },
    {
      title: ProductsType.PREFON,
      subtitle: 'Prévoyance Fonctiçonnaires',
      bodyTitle: 'Qu’est ce qu’un PREFON ? ',
      body: "PREFON est un régime de retraite complémentaire facultatif par capitalisation, ce qui signifie que les cotisations versées sont investies dans des fonds pour produire un rendement et constituer une épargne retraite. Les cotisations à PREFON sont déductibles d'impôt jusqu'à certaines limites, ce qui en fait un moyen attractif pour les fonctionnaires et agents de l'État de se constituer une épargne retraite complémentaire tout en bénéficiant d'avantages fiscaux. Ainsi, les cotisations versées sont investies dans des fonds en euros ou en unités de compte, et les intérêts générés sont capitalisés pour produire un revenu à la retraite.",
      objectives: [
        ProductsObjective.RETIREMENT_OBJECTIVE,
        ProductsObjective.WEALTH_OBJECTIVE,
        ProductsObjective.DEFISC_OBJECTIVE,
      ],
      riskRange: [1, 7],
      sector: [ProductSector.ASSET_SECTOR],
      titleLink: 'le PREFON',
    },
    {
      title: ProductsType.COMPTES_TITRES,
      subtitle: '',
      bodyTitle: 'Qu’est ce qu’un compte-titres ? ',
      body: "Un compte-titres est un compte financier qui permet à son titulaire d'acheter, de détenir et de vendre des titres de valeurs mobilières telles que des actions, des obligations, des fonds communs de placement, etc. C'est un compte utilisé par les investisseurs pour investir dans des valeurs mobilières et qui leur permet de suivre l'évolution de leur portefeuille de titres et de réaliser des opérations de trading. En d'autres termes, un compte-titres est un outil financier permettant de gérer des investissements en bourse.",
      objectives: [
        ProductsObjective.WEALTH_OBJECTIVE,
        ProductsObjective.DIVERSIFICATION_OBJECTIVE,
        ProductsObjective.INCOME_OBJECTIVE,
        ProductsObjective.TRANSMISSION_OBJECTIVE,
      ],
      riskRange: [1, 7],
      sector: [ProductSector.ASSET_SECTOR],
      titleLink: 'le compte-titres',
    },
    {
      title: ProductsType.TANGIBLES,
      subtitle: '',
      bodyTitle: 'Qu’est ce qu’un actif tangible ? ',
      body: 'Les actifs sont considérés comme « tangibles » dès lors qu’ils sont matériels. Indépendants des marchés financiers, ils constituent une solution sûre pour un investisseur face à une éventuelle crise. L’or, l’immobilier, les voitures de collection, l’art… Plus ces actifs deviennent rares dans le temps et plus ils prennent de la valeur.',
      objectives: [ProductsObjective.DIVERSIFICATION_OBJECTIVE],
      riskRange: [2, 7],
      sector: [ProductSector.ASSET_SECTOR],
      titleLink: "l'actif tangible",
    },
    {
      title: ProductsType.CROWDFUNDING,
      subtitle: '',
      bodyTitle: 'Qu’est ce que le crowdfunding ?',
      body: 'Beaucoup plus large que le don simple, avec ou sans contrepartie, le crowdfunding prend plusieurs formes : crowdfunding en capital, sous forme de prêts rémunérés... Hormis les projets qui vous tiennent à cœur, vous pouvez utiliser le financement participatif comme support d’épargne en investissant dans des secteurs tels que l’immobilier.',
      objectives: [
        ProductsObjective.RENT_OBJECTIVE,
        ProductsObjective.INCOME_OBJECTIVE,
      ],
      riskRange: [5],
      sector: [ProductSector.ASSET_SECTOR],
      titleLink: 'le crowdfunding',
      srcImg: 'assets/img/obj-cf.png',
      srcImgResp: 'assets/img/obj-cf-mob.png',
    },
    {
      title: ProductsType.PERP,
      subtitle: '(Plan d’épargne retraite populaire)',
      bodyTitle: 'Qu’est ce que le PERP ?',
      body: "Le Plan d’Epargne Retraite Populaire est un placement financier destiné à la constitution d'une épargne en vue de la retraite. Il permet de bénéficier d'une déduction fiscale sur les sommes investies chaque année, dans la limite d'un plafond qui dépend de l'âge de l'épargnant. Au moment de la retraite, l'épargnant pourra choisir de percevoir une rente viagère ou un capital. Comme pour une assurance-vie, l'épargnant a le choix entre différents supports d'investissement, qui lui permettent de diversifier son épargne et de gérer son niveau de risque.",
      objectives: [
        ProductsObjective.RETIREMENT_OBJECTIVE,
        ProductsObjective.TRANSMISSION_OBJECTIVE,
        ProductsObjective.RENT_OBJECTIVE,
      ],
      riskRange: [1, 7],
      sector: [ProductSector.ASSET_SECTOR],
      routerLink: '/dashboard/sheets/$route/per',
      titleLink: 'le PERP',
    },
    {
      title: ProductsType.DEFISCALISATION,
      subtitle: '',
      bodyTitle: 'Qu’est ce que les produits de défiscalisation ',
      body: 'Les produits de défiscalisation représente l’ensemble des solutions mises place par le gouvernements afin de réduire/ déduire vos impôts ou de bénéficier d’un crédit d’impôt. On peut distinguer les dispositifs immobiliers (Pinel, Malraux) de ceux d’épargne (PER, Girardin...)',
      objectives: [
        ProductsObjective.WEALTH_OBJECTIVE,
        ProductsObjective.DEFISC_OBJECTIVE,
      ],
      riskRange: [6],
      sector: [ProductSector.ASSET_SECTOR],
      titleLink: 'les produits de défiscalisation',
    },
    {
      title: ProductsType.CONTRAT_CAPI,
      bodyTitle: 'Qu’est ce qu’un contrat de capitalisation ?',
      body: "Un contrat de capitalisation est un produit d'investissement permettant d'accumuler du capital en vue de le faire fructifier. Contrairement à l'assurance-vie, il n'a pas de vocation assurantielle et n'offre pas de garantie de capital. Il est principalement utilisé pour des objectifs de placement et de transmission de patrimoine. Les versements peuvent être effectués de manière ponctuelle ou régulière.",
      objectives: [
        ProductsObjective.RENT_OBJECTIVE,
        ProductsObjective.RETIREMENT_OBJECTIVE,
        ProductsObjective.TRANSMISSION_OBJECTIVE,
      ],
      riskRange: [1, 7],
      sector: [ProductSector.ASSET_SECTOR],
      color: '#C24694',
      titleLink: 'le contrat de capitalisation',
    },
    {
      ...this.fiscalityObject,
    },
  ];

  secondaryProducts = [
    {
      title: 'ASSURANCE_VIE',
      body: 'Une assurance vie est un contrat dédié aux investisseurs souhaitant épargner pour faire fructifier son épargne, bénéficier d’un complément de retraite ou transmettre un héritage. Il s’agit d’une enveloppe fiscale permettant de loger des placements financiers plus ou moins risqués. Les versements peuvent s’effectuer de manière unitaire ou périodique. ',
      routerLink: '/dashboard/sheets/$route/av',
      objectives: [
        ProductsObjective.RENT_OBJECTIVE,
        ProductsObjective.RETIREMENT_OBJECTIVE,
        ProductsObjective.TRANSMISSION_OBJECTIVE,
      ],
      srcImg: 'assets/img/obj-av.png',
      srcImgResp: 'assets/img/obj-av-mob.png',
    },
    {
      title: 'BUSINESS_CENTER',
      body: "Investir dans des centres d'affaires sur Paris tout en bénéficiant d'une réduction d'impôts de 25% du montant investi.",
      objectives: '',
      srcImg: 'assets/img/obj-bc.png',
      srcImgResp: 'assets/img/obj-bc-mob.png',
    },
    {
      title: ProductsType.LMNP,
      subtitle: '(Location meublée non professionnelle)',
      bodyTitle: 'Qu’est ce  qu’une Lmnp ?',
      body: 'La LMNP, Loueur en Meublé Non Professionnel, est un dispositif qui vous permet de défiscaliser vos revenus locatifs. Pour bénéficier de cet avantage il vous suffit de louer votre bien en meublé. Il est accessible à tous et s’adresse aux investisseurs qui souhaitent investir dans un bien immobilier pour se constituer un patrimoine sur le long terme.',
      objectives: [
        ProductsObjective.WEALTH_OBJECTIVE,
        ProductsObjective.OWNER_OBJECTIVE,
        ProductsObjective.RENT_OBJECTIVE,
        ProductsObjective.INCOME_OBJECTIVE,
      ],
      riskRange: [2, 3],
      sector: [ProductSector.ESTATE_SECTOR],
      routerLink: '/dashboard/sheets/$route/lmnp',
      color: '#73A4F6',
      titleLink: 'la LMNP',
      srcImg: 'assets/img/obj-lmnp.png',
      srcImgResp: 'assets/img/obj-lmnp-mob.png',
    },
    {
      title: 'PME',
      body: 'Investissement dans des sociétés non cotés en bourses via des fonds de private equity réservés aux grandes fortunes.',
      objectives: '',
    },
    {
      title: 'MADELIN',
      body: "La retraite Madelin est un contrat permettant au chef d'entreprise de se constituer une retraite supplémentaire servie sous forme de rente viagère.",
      objectives: '',
      srcImg: 'assets/img/obj-madelin.png',
      srcImgResp: 'assets/img/obj-madelin-mob.png',
    },
    {
      title: 'MONUMENT_HISTORIQUE',
      body: "Investissement permettant d'investir dans un monument historique et ouvrant droit à une réduction d'impôt via sa restauration.",
      objectives: '',
      srcImg: 'assets/img/obj-mon-histo.png',
      srcImgResp: 'assets/img/obj-mon-histo-mob.png',
    },
    {
      title: 'LOAN',
      body: "Renégocier son prêt permet de réaliser des économies sur les intérêts d'emprunt.",
      objectives: '',
      srcImg: 'assets/img/obj-credit.png',
      srcImgResp: 'assets/img/obj-credit-mob.png',
    },
    {
      title: 'BORROWER_INSURANCE',
      body: "Une délégation d'assurance emprunteur vous permet d'obtenir un meilleur tarif en disposant des mêmes couvertures en cas de sinistre.",
      objectives: '',
      srcImg: 'assets/img/obj-av-emp.png',
      srcImgResp: 'assets/img/obj-av-emp-mob.png',
    },
    {
      title: 'DISMEMBERSHIP',
      body: "Le démembrement de propriété permet dans le cadre d'une donation avec démembrement de propriété de réduire les droits à payer à l'État.",
      objectives: '',
    },
    {
      title: 'CONTINGENCY',
      body: "La prévoyance permet de faire face aux risques de la vie comme la maladie, l'accident ou encore la dépendance, entraînant une incapacité.",
      objectives: '',
      srcImg: 'assets/img/obj-prev.png',
      srcImgResp: 'assets/img/obj-prev-mob.png',
    },
    {
      title: 'TREASURY_MANAGMENT',
      body: "Différentes stratégies sont possibles pour optimiser la trésorerie d'une société et mieux la placer qu'en la laissant à la banque.",
      objectives: '',
      srcImg: 'assets/img/treso.png',
      srcImgResp: 'assets/img/treso-mob.png',
    },
    {
      title: 'PEA',
      body: "Le PEA est destiné à favoriser l'épargne investie dans les entreprises, permettant aux investisseurs qui maintenaient leur investissement de bénéficier d'avantages fiscaux.",
      srcImg: 'assets/img/obj-pea-cap.png',
      srcImgResp: 'assets/img/obj-pea-cap-mob.png',
    },
    {
      title: 'OCP_BUSINESS_CENTER',
      body: "L'investissement permettant d'acheter des parts de capital de la société qui acquiert des biens immobiliers, déduisant 25% du montant de l'IR.",
      srcImg: 'assets/img/obj-ocp-bc.png',
      srcImgResp: 'assets/img/obj-ocp-bc-mob.png',
    },
    {
      title: 'OCP_CLUB_DEAL',
      body: "Investir dans des centres d'affaires sur Paris tout en bénéficiant d'un coupon garanti de 5% par an.",
      srcImg: 'assets/img/obj-ocp-cd.png',
      srcImgResp: 'assets/img/obj-ocp-cd-mob.png',
    },
    {
      title: 'CAT',
      body: "Les comptes à terme permettent de placer une somme d'argent pour une durée déterminée avec un taux d'intérêt fixe. C'est un produit d'investissement sans risque mais avec une liquidité limitée.",
      srcImg: 'assets/img/obj-cat.png',
      srcImgResp: 'assets/img/obj-cat-mob.png',
    },
    {
      title: 'BLOKS',
      body: "L'investissement en parts de blocs permet aux particuliers d'acquérir une fraction d'un bien immobilier ou d'un portefeuille de biens. Cela leur donne accès à des revenus locatifs proportionnels à leur part.",
      srcImg: 'assets/img/obj-bloks.png',
      srcImgResp: 'assets/img/obj-bloks-mob.png',
    },
  ];
  objectives = [
    {
      id: ProductsObjective.WEALTH_OBJECTIVE,
      label: 'Création de patrimoine',
      color: '#e7f6ed',
    },
    {
      id: ProductsObjective.DEFISC_OBJECTIVE,
      label: 'Défiscalisation',
      color: '#fef8e9',
    },
    {
      id: ProductsObjective.INCOME_OBJECTIVE,
      label: 'Revenus complémentaires',
      color: '#efecff',
    },
    {
      id: ProductsObjective.RENT_OBJECTIVE,
      label: 'Rendement',
      color: '#e7f3ff',
    },
    {
      id: ProductsObjective.OWNER_OBJECTIVE,
      label: 'Devenir propriétaire',
      color: '#fcedfa',
    },
    {
      id: ProductsObjective.RETIREMENT_OBJECTIVE,
      label: 'Préparer sa retraite',
      color: '#ffecec',
    },
    {
      id: ProductsObjective.TRANSMISSION_OBJECTIVE,
      label: 'Transmission',
      color: '#edfcfd',
    },
    {
      id: ProductsObjective.DIVERSIFICATION_OBJECTIVE,
      label: 'Diversification',
      color: '#FEF1E4',
    },
  ];

  objectiveById;

  constructor(private router: Router) {
    this.objectiveById = this.objectives.reduce((result, objective) => {
      result[objective.id] = objective;
      return result;
    }, {});
  }

  navigateToProduct(productType?: ProductsType) {
    let product = this.products.find(p => p.title == productType);
    this.router.navigate([`/dashboard/sheets/produits`], {
      queryParams: {
        objectives: product.objectives,
        sector: product.sector[0],
      },
    });
  }

  getProducts(): Product[] {
    return this.products;
  }

  getObjective(id: string): any {
    return this.objectiveById[id];
  }

  getObjectives(): any[] {
    return this.objectives;
  }

  getSectors(): any[] {
    return this.sectors;
  }

  findProductByTitle(title: string) {
    if (title == 'FISCALITY_RENT') return this.fiscalityObject;
    return this.products.find(product => product.title == title);
  }

  completeProductInfo(inputProduct: any): any {
    let productType = inputProduct.productType || inputProduct.product;

    if (!productType) {
      return inputProduct;
    }

    const matchingProduct =
      this.products.find(p => p.title === ProductsType[productType]) ||
      this.secondaryProducts.find(p => p.title === productType);

    if (matchingProduct) {
      return {
        ...inputProduct,
        body: matchingProduct.body,
        routerLink: matchingProduct.routerLink,
        objectives: matchingProduct.objectives,
        src: matchingProduct.srcImg,
        srcResp: matchingProduct.srcImgResp,
      };
    }

    return inputProduct;
  }
}
